import React, { useEffect, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { verifyRoomInviteToken } from "../../../../api/RoomsController";
import InviteRedirect from "./InviteRedirect";
import InviteExpired from "./InviteExpired";
import LoadingIcon from "../../../../components/common/loading/LoadingIcon";
import DefaultStream from "../../../../features/vagon/DefaultStream";
import { useStreaming } from "../../../../context/StreamingContext";
import { RoomSettingsProvider } from "../../../render-stream/RoomSettingsContext";
import RenderStreaming from "../../../render-stream/RenderStreaming";

const RoomInvitePage = () => {
    const user = useSelector((state) => state.user);
    const navigate = useNavigate();
    const { roomInviteLinkId, roomId } = useParams();
    const [isValid, setIsValid] = useState(null);
    const [respErrMessage, setRespErrMessage] = useState("");
    const { isDcvStreaming } = useStreaming();

    const fetchVerifyRoomInvite = useCallback(async () => {
        if (!roomInviteLinkId) return null;
        try {
            const res = await verifyRoomInviteToken(roomInviteLinkId);
            return res;
        } catch (err) {
            return {
                status: err?.response?.status || 500,
                response: err?.response || {
                    data: { message: "Unknown error", isValid: false },
                },
            };
        }
    }, [roomInviteLinkId]);

    const { data: tokenVerification, isLoading } = useQuery(
        ["verifyRoomInviteToken", roomInviteLinkId],
        fetchVerifyRoomInvite,
        {
            enabled: !!roomInviteLinkId,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    useEffect(() => {
        if (tokenVerification) {
            const { status, data, response } = tokenVerification;

            if (status === 400 || status === 403) {
                setIsValid(response.data.isValid);
                setRespErrMessage(response.data.message);
            }

            if (status === 200) {
                const decoded = data?.decodedToken;
                if (decoded?.roomInviteLinkId !== roomInviteLinkId) {
                    navigate(`/${roomId}/invite/${decoded.roomInviteLinkId}`, { replace: true });
                    return;
                }
                if (data.isValid && roomId === decoded.roomId) {
                    localStorage.setItem("inviteRoomId", roomId);
                    const inviteSettingsWithToken = {
                        ...decoded.inviteSettings,
                        token: decoded.token,
                        roomInviteLinkId,
                    };
                    localStorage.setItem("inviteSettings", JSON.stringify(inviteSettingsWithToken));
                    setIsValid(true);
                } else {
                    setIsValid(false);
                    setRespErrMessage("Invalid room ID.");
                }
            }
            if (status === 500) {
                setIsValid(false);
                setRespErrMessage("Internal server error.");
            }
        }
    }, [tokenVerification, user, roomId, roomInviteLinkId, navigate]);

    const renderContent = () => {
        if (isLoading || isValid === null) {
            return (
                <LoadingDiv>
                    <LoadingIcon />
                </LoadingDiv>
            );
        }

        if (isValid === true) {
            if (user?.userId) {
                // Route based on streaming mode
                if (isDcvStreaming) {
                    return (
                        <RoomSettingsProvider>
                            <RenderStreaming roomId={roomId} />
                        </RoomSettingsProvider>
                    );
                }
                return <DefaultStream userId={user.userId} token={user.token} roomId={roomId} />;
            }
            // console.log("User not logged in but invite is valid — redirecting to invite login");
            return <InviteRedirect roomId={roomId} />;
        }

        return <InviteExpired message={respErrMessage} />;
    };

    return <>{renderContent()}</>;
};

export default RoomInvitePage;

const LoadingDiv = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20rem;
`;
